import type { LepcState, OfficerProfile, TemplateData } from "./types";
import { TEMPLATES } from "./templates-index";

const STORAGE_KEY = "sentinel-lepc-state";

function emptyTemplateData(): TemplateData {
  return {
    fields: {},
    repeatables: {},
    checklists: {},
    checklistNotes: {},
    status: "not_started",
    lastSaved: "",
  };
}

export function loadState(): LepcState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) as LepcState;
    // Ensure all templates have entries
    for (const tpl of TEMPLATES) {
      if (!parsed.templates[tpl.id]) {
        parsed.templates[tpl.id] = emptyTemplateData();
      }
    }
    return parsed;
  } catch {
    return defaultState();
  }
}

export function defaultState(): LepcState {
  const templates: Record<string, TemplateData> = {};
  for (const tpl of TEMPLATES) {
    templates[tpl.id] = emptyTemplateData();
  }
  return {
    officer: null,
    onboarded: false,
    templates,
  };
}

export function saveState(state: LepcState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error("Failed to save LEP-C state:", e);
  }
}

export function saveOfficer(officer: OfficerProfile): LepcState {
  const state = loadState();
  state.officer = officer;
  state.onboarded = true;
  saveState(state);
  return state;
}

export function saveTemplateData(templateId: string, data: TemplateData): void {
  const state = loadState();
  state.templates[templateId] = data;
  saveState(state);
}

export function getTemplateData(templateId: string): TemplateData {
  const state = loadState();
  return state.templates[templateId] ?? emptyTemplateData();
}

export function resetTemplateData(templateId: string): void {
  const state = loadState();
  state.templates[templateId] = emptyTemplateData();
  saveState(state);
}

export function markTemplateComplete(templateId: string, complete: boolean): void {
  const state = loadState();
  const data = state.templates[templateId] ?? emptyTemplateData();
  data.status = complete ? "complete" : "in_progress";
  data.lastSaved = new Date().toISOString();
  state.templates[templateId] = data;
  saveState(state);
}

export function getCompletionStats() {
  const state = loadState();
  let complete = 0;
  let inProgress = 0;
  let notStarted = 0;

  for (const tpl of TEMPLATES) {
    const data = state.templates[tpl.id];
    if (!data || data.status === "not_started") notStarted++;
    else if (data.status === "complete") complete++;
    else inProgress++;
  }

  return { complete, inProgress, notStarted, total: TEMPLATES.length };
}

export function getModuleCompletion(moduleId: number): { complete: number; total: number } {
  const state = loadState();
  const moduleTemplates = TEMPLATES.filter((t) => t.module === moduleId);
  let complete = 0;
  for (const tpl of moduleTemplates) {
    const data = state.templates[tpl.id];
    if (data && data.status === "complete") complete++;
  }
  return { complete, total: moduleTemplates.length };
}

export function getDaysRemaining(): number | null {
  const state = loadState();
  if (!state.officer?.targetCompletionDate) return null;
  const target = new Date(state.officer.targetCompletionDate);
  const now = new Date();
  const diff = target.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function isOnboarded(): boolean {
  const state = loadState();
  return state.onboarded && state.officer !== null;
}

export function getOfficer(): OfficerProfile | null {
  const state = loadState();
  return state.officer;
}

export function clearAllData(): void {
  localStorage.removeItem(STORAGE_KEY);
}
